import React, {createContext, useContext, useEffect, useRef, useState} from 'react';
import {listAllItemsRequest} from 'util/api';
import AllItemsData from 'util/market/AllItemsData';
import ItemData from 'util/market/ItemData';
import {WebSocketContext} from 'wss/WebSocketService';
import {getAllEmojis, getAllLetters} from 'util/market/itemToAllowedText';
import {UserContext} from "../../components/user/UserContext";

export const MarketContext = createContext();

export const MarketStateProvider = ({children}) => {
    const wsService = useContext(WebSocketContext);
    const marketStateRef = useRef({items: []});
    const [allItems, setAllItems] = useState([]);
    const [allowedLetters, setAllowedLetters] = useState([]);
    const [allowedEmojis, setAllowedEmojis] = useState([]);
    const {user} = useContext(UserContext);

    // Fetch market data on mount
    useEffect(() => {
        if (!user?.isLoggedIn) return;

        const fetchMarketData = async () => {
            try {
                const marketData = await listAllItemsRequest();
                const allItemsData = new AllItemsData(marketData);
                marketStateRef.current = {
                    items: allItemsData.getAllItems(),
                };
                setAllItems(marketStateRef.current.items);
            } catch (error) {
                console.error('Error fetching market data:', error);
            }
        };

        fetchMarketData();
    }, [user?.isLoggedIn]);

    useEffect(() => {
        setAllowedLetters(getAllLetters(allItems));
        setAllowedEmojis(getAllEmojis(allItems));
    }, [allItems]);

    useEffect(() => {
        const handleDelta = (delta) => {
            let itemData = marketStateRef.current.items.find(
                (item) => item.itemType === delta.itemType
            );

            if (!itemData) {
                itemData = new ItemData({itemType: delta.itemType});
                marketStateRef.current.items.push(itemData);
            }

            if (!itemData.user) {
                itemData.user = {};
            }

            if (delta.marketQuantityDelta) {
                itemData.market.count = delta.marketQuantity;
            }
            if (delta.bestSoDelta) {
                itemData.market.bestSo = delta.bestSo;
            }
            if (delta.bestBoDelta) {
                itemData.market.bestBo = delta.bestBo;
            }
            if (delta.userQuantityDelta) {
                itemData.user.count = delta.userQuantity;
            }
            if (delta.userSellOfferDelta) {
                itemData.user.sellOffer = delta.userSellOffer;
            }
            if (delta.userBuyOfferDelta) {
                itemData.user.buyOffer = delta.userBuyOffer;
            }
        };

        const handleMessage = (content, topic) => {
            if (topic !== 'marketItem') {
                console.warn('Received message for unknown topic:', topic);
                return;
            }

            if (Array.isArray(content)) {
                content.forEach((item) => handleDelta(item));
            } else {
                handleDelta(content);
            }

            // Update the allItems state
            setAllItems([...marketStateRef.current.items]);
        };

        wsService.connect();
        wsService.bindChannel('market');
        wsService.onMessage('market', handleMessage);

        // Cleanup on unmount
        return () => {
            wsService.unbindChannel('market');
        };
    }, [wsService]);

    return (
        <MarketContext.Provider value={{allItems, allowedLetters, allowedEmojis}}>
            {children}
        </MarketContext.Provider>
    );
};
